import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bike, MapPin, Phone, Package, CheckCircle2, Navigation, LogOut, Clock, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { eur, STATUS_LABELS, STATUS_COLORS, fmtDate, PAYMENT_LABELS } from '@/lib/format';
import { toast } from 'sonner';

export default function RiderHome() {
  const [orders, setOrders] = useState([]);
  const [available, setAvailable] = useState(true);
  const { user, logout } = useAuthStore();
  const nav = useNavigate();
  const watchIdRef = useRef(null);

  const load = () => {
    api.get('/rider/orders').then(r => setOrders(r.data)).catch(() => {});
  };

  useEffect(() => { setAvailable(user?.active !== false); }, [user]);
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;
    if (!available) {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      return;
    }
    const post = (lat, lng) => api.post('/rider/location', { lat, lng }).catch(() => {});
    // initial
    navigator.geolocation.getCurrentPosition(
      (pos) => post(pos.coords.latitude, pos.coords.longitude),
      () => {},
      { enableHighAccuracy: true, timeout: 8000 },
    );
    const id = navigator.geolocation.watchPosition(
      (pos) => post(pos.coords.latitude, pos.coords.longitude),
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    watchIdRef.current = id;
    return () => { if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current); };
  }, [available]);

  const toggleAvailability = async (v) => {
    setAvailable(v);
    try { await api.patch('/rider/availability', { available: v }); }
    catch { toast.error('Errore aggiornamento disponibilità'); }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/rider/orders/${orderId}/status`, { status });
      toast.success('Aggiornato');
      load();
    } catch { toast.error('Errore'); }
  };

  const openMaps = (order) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${order.delivery_lat},${order.delivery_lng}&travelmode=driving`;
    window.open(url, '_blank');
  };

  const activeOrder = orders[0];

  return (
    <div className="min-h-screen bg-[--brand-cream]">
      <header className="bg-white border-b border-[--brand-border] sticky top-0 z-30" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-md mx-auto px-4 h-14 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[--brand-accent] text-white flex items-center justify-center"><Bike size={18}/></div>
          <div className="flex-1">
            <div className="font-display font-bold text-sm">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-[--brand-muted]">App rider</div>
          </div>
          <Link to="/rider/history"><Button variant="ghost" size="icon" className="rounded-lg"><Clock size={16}/></Button></Link>
          <Button variant="ghost" size="icon" className="rounded-lg" onClick={() => { logout(); nav('/rider/login'); }} data-testid="rider-logout-button"><LogOut size={16}/></Button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-4 pb-24">
        <div className="rounded-2xl bg-white border border-[--brand-border] p-4 flex items-center justify-between">
          <div>
            <div className="font-display font-bold">Disponibilità</div>
            <div className="text-xs text-[--brand-muted]">{available ? 'Sei online per ricevere ordini' : 'Non riceverai ordini'}</div>
          </div>
          <Switch checked={available} onCheckedChange={toggleAvailability} data-testid="rider-availability-toggle"/>
        </div>

        {activeOrder ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl bg-white border border-[--brand-border] p-4 shadow-[var(--shadow-md)]"
                      data-testid="rider-active-order">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-display text-xl font-bold">#{activeOrder.order_number}</div>
                <div className="text-xs text-[--brand-muted]">{fmtDate(activeOrder.created_at)}</div>
              </div>
              <Badge className={`${STATUS_COLORS[activeOrder.status]} border rounded-full`}>{STATUS_LABELS[activeOrder.status]}</Badge>
            </div>

            <div className="rounded-xl bg-[--brand-surface-2] p-3 space-y-2 text-sm mt-2">
              <div className="flex items-center gap-2"><User size={14} className="text-[--brand-muted]"/>{activeOrder.customer_name}</div>
              <a href={`tel:${activeOrder.customer_phone}`} className="flex items-center gap-2 text-[--brand-primary] font-semibold">
                <Phone size={14}/> {activeOrder.customer_phone}
              </a>
              <div className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 text-[--brand-muted]"/><div>{activeOrder.delivery_address}</div></div>
              {activeOrder.delivery_notes && <div className="text-xs italic text-[--brand-muted]">Note: {activeOrder.delivery_notes}</div>}
            </div>

            <div className="mt-3 space-y-1 text-sm">
              {activeOrder.items.map((it, i) => (
                <div key={i} className="flex justify-between">
                  <span>{it.quantity}× {it.name}</span>
                  <span className="tabular-nums">{eur(it.unit_price * it.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-[--brand-border] flex items-center justify-between">
              <div className="text-xs text-[--brand-muted]">{PAYMENT_LABELS[activeOrder.payment_method]}</div>
              <div className="tabular-nums font-display font-bold text-lg text-[--brand-primary]">Totale {eur(activeOrder.total)}</div>
            </div>

            <Button onClick={() => openMaps(activeOrder)}
                    className="w-full mt-4 rounded-xl bg-[--brand-accent] hover:bg-[--brand-accent-hover] text-white h-14 text-base"
                    data-testid="rider-open-maps-button">
              <Navigation size={20} className="mr-2"/> Apri Google Maps
            </Button>

            <div className="mt-2 space-y-2">
              {activeOrder.status === 'ready' && (
                <Button onClick={() => updateStatus(activeOrder.id, 'in_delivery')}
                        className="w-full rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover] h-12"
                        data-testid="rider-status-picked-up-button">
                  <Package size={18} className="mr-2"/> Ho ritirato
                </Button>
              )}
              {activeOrder.status === 'in_delivery' && (
                <Button onClick={() => updateStatus(activeOrder.id, 'delivered')}
                        className="w-full rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover] h-12"
                        data-testid="rider-status-delivered-button">
                  <CheckCircle2 size={18} className="mr-2"/> Consegnato
                </Button>
              )}
            </div>
          </motion.div>
        ) : (
          <div className="rounded-2xl bg-white border border-[--brand-border] p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[--brand-surface-2] mx-auto flex items-center justify-center"><Bike size={28} className="text-[--brand-muted]"/></div>
            <div className="font-display font-bold mt-3">Nessun ordine attivo</div>
            <div className="text-sm text-[--brand-muted] mt-1">Attendi che ti venga assegnato un ordine</div>
          </div>
        )}

        {orders.slice(1).length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-[--brand-muted] mb-2">In coda</div>
            {orders.slice(1).map(o => (
              <div key={o.id} className="rounded-xl bg-white border border-[--brand-border] p-3 mb-2">
                <div className="flex justify-between">
                  <div className="font-semibold text-sm">#{o.order_number}</div>
                  <Badge className={`${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</Badge>
                </div>
                <div className="text-xs text-[--brand-muted]">{o.delivery_address}</div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
